/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Pack PDF de QRs por mesa: /m/{slug}?t={public_token}
 * Branding opcional: logo + nombre del local (public_store_profiles.logo_url).
 */
import React, { useCallback, useState } from 'react';
import {
  ActivityIndicator,
  Share,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import * as FileSystem from 'expo-file-system/legacy';
import * as Sharing from 'expo-sharing';
import QRCodeGenerator from 'qrcode';
import { colors } from '../../shared/theme/colors';
import { ticketColors } from '../../shared/theme/ticketColors';
import { spacing } from '../../shared/theme/spacing';
import { typography } from '../../shared/theme/typography';
import { showError, showInfo, showSuccess } from '../../shared/ui/appToast';
import { menuPublicUrl } from '../../settings/domain/storeProfileTypes';
import type { DiningTable } from '../domain/hospitalityTypes';

const PORTAL_BASE = process.env.EXPO_PUBLIC_CUSTOMER_PORTAL_URL ?? 'https://www.vuelto.ar';

export type TableQrPackSectionProps = {
  slug: string | null | undefined;
  branchName: string;
  /** Optional store display name (falls back to branchName). */
  storeName?: string | null;
  /** Optional logo from public_store_profiles.logo_url. */
  logoUrl?: string | null;
  tables: DiningTable[];
};

async function fetchLogoDataUrl(logoUrl: string): Promise<{ dataUrl: string; format: 'PNG' | 'JPEG' } | null> {
  try {
    const res = await fetch(logoUrl);
    if (!res.ok) return null;
    const contentType = (res.headers.get('content-type') ?? '').toLowerCase();
    const buf = await res.arrayBuffer();
    const bytes = new Uint8Array(buf);
    let binary = '';
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    const base64 = btoa(binary);
    const isJpeg =
      contentType.includes('jpeg') ||
      contentType.includes('jpg') ||
      /\.jpe?g(\?|$)/i.test(logoUrl);
    const mime = isJpeg ? 'image/jpeg' : 'image/png';
    return {
      dataUrl: `data:${mime};base64,${base64}`,
      format: isJpeg ? 'JPEG' : 'PNG',
    };
  } catch {
    return null;
  }
}

export function TableQrPackSection({
  slug,
  branchName,
  storeName,
  logoUrl,
  tables,
}: TableQrPackSectionProps) {
  const [busy, setBusy] = useState(false);
  const withToken = tables.filter((t) => Boolean(t.publicToken?.trim()));
  const displayName = (storeName?.trim() || branchName || 'Mi local').trim();

  const handleDownloadPack = useCallback(async () => {
    if (!slug?.trim()) {
      showInfo('Falta el link', 'Guardá el perfil de la tienda con un slug para armar los QR.');
      return;
    }
    if (withToken.length === 0) {
      showInfo('Sin tokens', 'Las mesas todavía no tienen QR. Creá o actualizá una mesa.');
      return;
    }

    setBusy(true);
    try {
      const { jsPDF } = await import('jspdf');
      const pdf = new jsPDF({ unit: 'mm', format: 'a6' });
      const base = menuPublicUrl(PORTAL_BASE, slug.trim());
      const logo = logoUrl?.trim() ? await fetchLogoDataUrl(logoUrl.trim()) : null;

      for (let i = 0; i < withToken.length; i += 1) {
        const table = withToken[i];
        const url = `${base}?t=${encodeURIComponent(table.publicToken!.trim())}`;
        const dataUrl = await QRCodeGenerator.toDataURL(url, {
          width: 512,
          margin: 2,
          color: { dark: '#000000', light: '#FFFFFF' },
        });
        const base64 = dataUrl.split(',')[1];
        if (i > 0) pdf.addPage();

        let titleY = 14;
        if (logo) {
          try {
            pdf.addImage(logo.dataUrl, logo.format, 10, 8, 14, 14);
            titleY = 14;
            pdf.setFontSize(16);
            pdf.text(displayName, 28, titleY);
          } catch {
            pdf.setFontSize(16);
            pdf.text(displayName, 10, titleY);
          }
        } else {
          pdf.setFontSize(16);
          pdf.text(displayName, 10, titleY);
        }

        pdf.setFontSize(14);
        pdf.text(table.name, 10, 22);
        pdf.setFontSize(8);
        pdf.text(url, 10, 28, { maxWidth: 85 });
        pdf.addImage(`data:image/png;base64,${base64}`, 'PNG', 22, 34, 60, 60);
        pdf.setFontSize(9);
        pdf.text('Escaneá para pedir desde la mesa', 10, 104);
      }

      const pdfBase64 = pdf.output('datauristring').split(',')[1];
      const fileUri = `${FileSystem.cacheDirectory}mesas-qr-${slug.trim()}.pdf`;
      await FileSystem.writeAsStringAsync(fileUri, pdfBase64, {
        encoding: FileSystem.EncodingType.Base64,
      });

      if (await Sharing.isAvailableAsync()) {
        await Sharing.shareAsync(fileUri, {
          mimeType: 'application/pdf',
          dialogTitle: 'QR de mesas',
        });
      } else {
        await Share.share({ url: fileUri, message: `QR mesas · ${displayName}` });
      }
      showSuccess('Listo', `${withToken.length} QR en el PDF.`);
    } catch (e) {
      console.warn('[TableQrPack] PDF error', e);
      showError('No se pudo armar el PDF', 'Probá de nuevo en un toque.');
    } finally {
      setBusy(false);
    }
  }, [slug, displayName, logoUrl, withToken]);

  if (tables.length === 0) return null;

  return (
    <View style={styles.card}>
      <Text style={styles.title}>QR de cada mesa</Text>
      <Text style={styles.desc}>
        Un PDF con un QR por mesa
        {logoUrl?.trim() ? ' y el logo del local' : ''}. Lo imprimís y lo pegás en el salón.
      </Text>
      <TouchableOpacity
        style={[styles.btn, busy && styles.btnDisabled]}
        onPress={() => void handleDownloadPack()}
        disabled={busy}
        accessibilityRole="button"
        accessibilityLabel="Descargar pack PDF de QR de mesas"
        testID="hospitality.mesas.qr-pack"
      >
        {busy ? (
          <ActivityIndicator color={colors.surface} />
        ) : (
          <Text style={styles.btnText}>
            Descargar PDF ({withToken.length} mesa{withToken.length === 1 ? '' : 's'})
          </Text>
        )}
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    marginBottom: spacing.sm,
    paddingHorizontal: 14,
    paddingVertical: 12,
    gap: 8,
  },
  title: { ...typography.body, color: colors.textPrimary, fontWeight: '700' },
  desc: { ...typography.caption, color: colors.textSecondary, lineHeight: 18 },
  btn: {
    alignSelf: 'flex-start',
    minHeight: 40,
    borderRadius: 10,
    backgroundColor: ticketColors.gold,
    justifyContent: 'center',
    paddingHorizontal: 14,
  },
  btnDisabled: { opacity: 0.6 },
  btnText: { ...typography.body, color: ticketColors.goldInk, fontSize: 14, fontWeight: '700' },
});
